import SettingsIcon from '@images/svg/cogFull.svg?react';
import DotsIcon from '@images/svg/dots.svg?react';
import DownloadIcon from '@images/svg/download.svg?react';
import FilterIcon from '@images/svg/filter.svg?react';
import SortArrowIcon from '@images/svg/sortArrow.svg?react';
import EyeIcon from '@images/svg/eye.svg?react';
import { RxWidth } from 'react-icons/rx';
import EyeClosedIcon from '@images/svg/eyeClosed.svg?react';
import ToggleList, { ToggleListPosition } from '@components/primitives/toggleList/ToggleList';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { toLocalDate } from '@utils/dateUtils';
import { Table } from '@tanstack/table-core';
import { Entries } from '@models/_helpers/Entries';
import { NATIVE_COLUMNS } from '@components/primitives/dataGrid/DataGrid';
import { ColumnDef } from '@tanstack/react-table';
import useDataGridColumnAutoSizer from '@components/primitives/dataGrid/hooks/useDataGridColumnAutoSizer';

type CSVRow<TData> = {
    [key in keyof TData]: unknown;
};

interface CSVHeader {
    label: string;
    key: string;
}

interface Props<TData> {
    // eslint-disable-next-line
    columns: ColumnDef<TData, any>[];
    storageKey?: string;
    csvFileName: string;
    onOpenGridSettings: () => void;
    gridSettingsEnabled: boolean;
    onResetFilters: () => void;
    resetFiltersEnabled: boolean;
    resetFiltersVisible: boolean;
    onResetSorting: () => void;
    resetSortingEnabled: boolean;
    resetSortingVisible: boolean;
    onToggleShowFilters: () => void;
    toggleFiltersVisible: boolean;
    showFilters: boolean;
    table: Table<TData>;
}

const DataGridOptions = <TData extends object>({
    columns,
    storageKey,
    csvFileName,
    onOpenGridSettings,
    gridSettingsEnabled,
    onResetFilters,
    resetFiltersEnabled,
    resetFiltersVisible,
    onResetSorting,
    resetSortingEnabled,
    resetSortingVisible,
    onToggleShowFilters,
    toggleFiltersVisible,
    showFilters,
    table,
}: Props<TData>) => {
    const { t } = useTranslation();
    const headerGroups = table.getHeaderGroups();
    const { autoSizeColumn } = useDataGridColumnAutoSizer();

    const onAutoSizeColumns = () => {
        const visibleColumns = table.getVisibleFlatColumns();
        const columnsToResize = visibleColumns.filter((column) => !NATIVE_COLUMNS.includes(column.id));

        columnsToResize.forEach((column) => {
            autoSizeColumn(table, column.id);
        });
    };

    const csvData: Array<CSVRow<TData>> = table.getPrePaginationRowModel().rows.map((row) => {
        const escapedRow: CSVRow<TData> = {
            ...row.original,
        };

        (Object.entries(row.original) as Entries<TData>).forEach(([key, value]) => {
            const header = columns.find((column) => column.id === key);
            const headerFormat = header?.meta?.format;

            if (typeof value === 'string') {
                if (headerFormat === 'date') {
                    escapedRow[key] = format(toLocalDate(value), 'yyyy-MM-dd');
                } else if (headerFormat === 'time') {
                    escapedRow[key] = format(new Date(value), 'yyyy-MM-dd HH:mm');
                } else {
                    // Make sure we escape quotes in the text.
                    escapedRow[key] = value.replace(/"/g, '""');
                }
            } else if (typeof value === 'number') {
                // WSD-1751: Replace dot separators with comma for Excel.
                escapedRow[key] = value.toString().replace('.', ',');
            } else {
                escapedRow[key] = value;
            }
        });
        return escapedRow;
    });

    const csvHeaders: Array<CSVHeader> = headerGroups.map((headerGroup) => {
        return headerGroup.headers
            .filter((header) => {
                return !NATIVE_COLUMNS.includes(header.id);
            })
            .map((header) => {
                return {
                    label: header.column.columnDef.header as string,
                    key: header.column.id,
                };
            });
    })[0];

    return (
        <ToggleList
            className="dataGrid__options"
            position={ToggleListPosition.BottomRight}
            list={[
                [
                    {
                        id: 'resetFilters',
                        isHidden: !resetFiltersVisible,
                        enabled: resetFiltersEnabled,
                        title: t('resetFilters.button', { ns: 'dataGrid' }),
                        onClick: onResetFilters,
                        icon: <FilterIcon />,
                    },
                    {
                        id: 'resetSorting',
                        isHidden: !resetSortingVisible,
                        enabled: resetSortingEnabled,
                        title: t('resetSorting.button', { ns: 'dataGrid' }),
                        onClick: onResetSorting,
                        icon: <SortArrowIcon />,
                    },
                    {
                        id: 'toggleFilters',
                        isHidden: !toggleFiltersVisible || !storageKey,
                        enabled: true,
                        title: showFilters ? t('gridFilters.hide', { ns: 'dataGrid' }) : t('gridFilters.show', { ns: 'dataGrid' }),
                        onClick: onToggleShowFilters,
                        icon: showFilters ? <EyeClosedIcon /> : <EyeIcon />,
                    },
                    {
                        id: 'autoSizeColumns',
                        isHidden: !storageKey,
                        enabled: true,
                        title: t('options.autoSizeColumns', { ns: 'dataGrid' }),
                        onClick: onAutoSizeColumns,
                        icon: <RxWidth />,
                    },
                ],
                [
                    {
                        id: 'settings',
                        isHidden: !gridSettingsEnabled,
                        enabled: gridSettingsEnabled,
                        title: t('options.settings', { ns: 'dataGrid' }),
                        onClick: onOpenGridSettings,
                        icon: <SettingsIcon />,
                    },
                ],
                [
                    {
                        element: (
                            <CSVLink className="toggleList__item toggleList__item--hasIcon" filename={csvFileName} data={csvData} headers={csvHeaders} separator=";">
                                <div className="toggleList__item-icon">
                                    <DownloadIcon />
                                </div>
                                <span className="title">{t('options.exportToCSV', { ns: 'dataGrid' })}</span>
                            </CSVLink>
                        ),
                    },
                ],
            ]}
            trigger={(toggle) => (
                <button className="dataGrid__options-button" type="button" onClick={toggle}>
                    <DotsIcon />
                </button>
            )}
        />
    );
};

export default DataGridOptions;
